namespace $ {

	export function $shm_hitalama_map_runtime_make<
		View extends object,
	>(
		View: new()=> View,
		config: Record< string, any > = {},
	) {
		const obj = new View as any

		for( const name in config ) {
			const value = config[ name ]
			obj[ name ] = typeof value === 'function'
				? value
				: function() { return value }
		}

		return obj as View
	}

	export function $shm_hitalama_map_runtime_view_widget( widget?: null | $shm_hitalama_map_widget ) {
		if( !widget ) return undefined
		return widget.view_widget?.() ?? widget
	}

	export function $shm_hitalama_map_runtime_view_value(
		widget: $shm_hitalama_map_widget | undefined,
		fallback?: any,
	) {
		return ( next?: any )=> {
			const view = $shm_hitalama_map_runtime_view_widget( widget )
			if( !view ) return fallback
			const value = view.map().widget_value( view.link_str(), next )
			return value ?? fallback
		}
	}

	export function $shm_hitalama_map_runtime_view_data(
		widget: $shm_hitalama_map_widget | undefined,
		key: string,
		fallback?: any,
	) {
		return ( next?: any )=> {
			const view = $shm_hitalama_map_runtime_view_widget( widget )
			if( !view ) return fallback
			const data = view.data() ?? {}
			if( next === undefined ) return data[ key ] ?? fallback
			view.data({ ...data, [ key ]: next })
			return next
		}
	}

	export function $shm_hitalama_map_runtime_bind_value(
		map: $shm_hitalama_map,
		id: string,
		fallback?: any,
	) {
		return ( next?: any )=> {
			const value = map.widget_value( id, next )
			return value ?? fallback
		}
	}

	export class $shm_hitalama_map_runtime_runner extends $mol_object {

		on_change_states = new WeakMap< object, Map< string, { value: unknown } > >

		map() {
			return null as any as $shm_hitalama_map
		}

		ref() {
			return ''
		}

		widget() {
			return this.map().widget( this.ref() )
		}

		toString() {
			return `${ this.constructor.name }<${ this.ref() }>`
		}

		view() {
			return this.widget().view()
		}

		page() {
			const view = this.view() as
				| ( $mol_view & {
					Map_page?: ()=> $shm_hitalama_map_page
				} )
				| undefined

			return view?.Map_page?.()
		}

		context(): Record< string, any > {
			return {
				[ '$' + 'page' ]: this.page(),
				[ '$' + 'this_widget' ]: this.widget(),
				[ '$' + 'view' ]: this.view(),
			}
		}

		run_reactive_deps( code: string, context: Record< string, any > ) {
			const deps_code = this.map().runtime().extract_reactive_deps( code )
			if( deps_code ) this.map().execute( deps_code, context )
		}

		is_atom() {
			if( this.widget().type() === 'code_html' ) return true
			return this.map().runtime().code_is_atom( this.widget().code() )
		}

		on_change< Value >(
			host: Record< string, any >,
			prop: string,
			effect: ( next: Value, prev: Value )=> unknown,
		) {
			const reader = host[ prop ]
			if( typeof reader !== 'function' ) {
				throw new Error( `No method "${ prop }" for on_change` )
			}

			const next = reader.call( host ) as Value
			let states = this.on_change_states.get( host )

			if( !states ) {
				states = new Map
				this.on_change_states.set( host, states )
			}

			const prev = states.get( prop )
			if( prev && prev.value !== next ) effect( next, prev.value as Value )

			states.set( prop, { value: next } )
			return next
		}

		@ $shm_hitalama_process_mem
		run_auto( next?: null ) {
			const widget = this.widget()
			const code = widget.code()
			const context = this.context()
			this.run_reactive_deps( code, context )

			if( widget.type() === 'code_html' ) {
				return this.map().prepare_html( code )
			}
			if( widget.type() === 'code_sql' ) {
				return this.map().execute( $shm_hitalama_map_runtime_sql_code(code), {
					...context,
					[ '$' + 'arg' ]: this.sql_processed(),
				} )
			}

			return this.is_atom()
				? this.map().execute( code, context )
				: this.map().execute_action( code, context )
		}

		@ $mol_mem
		sql_processed() {
			return $shm_hitalama_map_runtime_sql_process(this.map(), this.widget().code_sql().sql())
		}

		@ $mol_mem
		run_if_enabled() {
			const widget = this.widget()
			if( !widget.enabled() ) return null
			return this.run_auto()
		}

	}

	export class $shm_hitalama_map_runtime extends $mol_object {

		map() {
			return null as any as $shm_hitalama_map
		}

		@ $mol_mem_key
		widget_runner( ref: string ) {
			return $shm_hitalama_map_runtime_runner.make({
				map: ()=> this.map(),
				ref: ()=> ref,
			})
		}

		@ $mol_mem
		components(): $shm_hitalama_map_component {
			return this.$.$shm_hitalama_map_component.make({ map: ()=> this.map() })
		}

		@ $mol_mem
		component_factory() { return this.$.$shm_hitalama_map_component_factory( this.components() ) }

		prepare_html( text: string ) {
			const init_code = `
				(()=> {
				const $ = window.parent;
				$.$shm_hitalama_compat_fqn_install($);
				const $${''}map = $.$giper_baza_glob.Pawn( new $.$giper_baza_link('${this.map().link().toString()}'), $.$shm_hitalama_map );
				const $${''}current_page = ()=> {
					const $${''}map_id = $.$mol_state_arg.value( 'map' );
					if( $${''}map_id ) return $.$shm_hitalama_app.Root(0).Maps().Book( $${''}map_id ).Map_page();
					if( $.$mol_state_arg.value( 'settings' ) === 'overlayed_map' ) return null;
					return null;
				};
				const $${''}page = null;
				const $${''}value = $${''}map.widget_value.bind($${''}map);
				const $${''}value_async = $${''}map.widget_value_async.bind($${''}map);
				const $${''}widget = $${''}map.widget.bind($${''}map);
				const $${''}run = $${''}map.widget_value_async.bind($${''}map);
				const $${''}data = $${''}map.widget_data.bind($${''}map);
				const $${''}text = $${''}map.widget_text.bind($${''}map);
				const $${''}file = $${''}map.widget_file.bind($${''}map);
				const $${''}store = $${''}map.store.bind($${''}map);
				const $${''}store_async = $${''}map.store_async.bind($${''}map);
				const $${''}temp_store = $${''}map.temp_store.bind($${''}map);
				const $${''}cursor_pos = ( $${''}page ?? $${''}current_page() )?.get_pointer_pos();
				const $${''}cors_proxy = $.$shm_hitalama_cors_proxy;
				const $${''}secret = $.$${''}shm_hitalama_profile.current()?.secret_val.bind($.$${''}shm_hitalama_profile.current());
				const $${''}map_by_id = (id)=> $.$giper_baza_glob.Pawn( new $.$giper_baza_link(id), $.$shm_hitalama_map );
				Object.assign(window, { $, $${''}map, $${''}current_page, $${''}page, $${''}value, $${''}value_async,
					$${''}widget, $${''}run, $${''}data, $${''}text, $${''}file, $${''}store, $${''}store_async, $${''}temp_store,
					$${''}cursor_pos, $${''}cors_proxy, $${''}secret, $${''}map_by_id });
				})();
			`
			// Keep bridge declarations out of the user's global lexical scope. A
			// document may legitimately declare its own const/let $, $map, etc.
			// A separate classic script also works before module/external scripts.
			return text.replace( /<script\b/i, ()=> `<script>${ init_code }</script><script` )
		}

		context_rich( context?: Record< string, any >, sync: 'async' | 'sync' = 'sync' ): Record< string, any > {
			const runtime = this
			const map = this.map()
			const widget = context?.[ '$' + 'this_widget' ] as $shm_hitalama_map_widget | undefined
			const page = context?.[ '$' + 'page' ] as $shm_hitalama_map_page | undefined
			const current_page = ()=> {
				const map_id = this.$.$mol_state_arg.value( 'map' )
				if( map_id ) return this.$.$shm_hitalama_app.Root(0).Maps().Book( map_id ).Map_page()
				if( this.$.$mol_state_arg.value( 'settings' ) === 'overlayed_map' ) return context?.[ '$' + 'page' ]
				return null
			}
			const context_public = Object.fromEntries(
				Object.entries( context ?? {} ).filter( ([ key ])=> key.startsWith( '$' ) )
			)

			return {
				[ '$' + 'map' ]: map,
				[ '$' + 'page' ]: page,
				[ '$' + 'value' ]: sync === 'async'
					? map?.widget_value_async.bind( map )
					: map?.widget_value.bind( map ),
				[ '$' + 'value_async' ]: map?.widget_value_async.bind( map ),
				[ '$' + 'widget' ]: map?.widget.bind( map ),
				[ '$' + 'run' ]: map?.widget_value_async.bind( map ),
				[ '$' + 'data' ]: map?.widget_data.bind( map ),
				[ '$' + 'text' ]: map?.widget_text.bind( map ),
				[ '$' + 'file' ]: map?.widget_file.bind( map ),
				[ '$' + 'store' ]: map?.store.bind( map ),
				[ '$' + 'store_async' ]: map?.store_async.bind( map ),
				[ '$' + 'temp_store' ]: map?.temp_store.bind( map ),
				get [ '$' + 'cursor_pos' ]() {
					return ( page ?? current_page() )?.get_pointer_pos()
				},
				[ '$' + 'cors_proxy' ]: this.$.$shm_hitalama_cors_proxy,
				get [ '$' + 'secret' ]() {
					const profile = runtime.$.$shm_hitalama_profile.current()
					return profile?.secret_val.bind( profile )
				},
				[ '$' + 'map_by_id' ]: ( id: string )=> this.$.$giper_baza_glob.Pawn( new this.$.$giper_baza_link( id ), this.$.$shm_hitalama_map ),
				[ '$' + 'to_table' ]: this.to_table,
				[ '$' + 'create_molwire_dom' ]: create_molwire_dom,
				[ '$' + 'make' ]: this.$.$shm_hitalama_map_runtime_make,
				[ '$' + 'component' ]: this.component_factory(),
				get [ '$' + 'view_widget' ]() {
					return runtime.$.$shm_hitalama_map_runtime_view_widget( widget )
				},
				[ '$' + 'view_value' ]: ( fallback?: any )=> this.$.$shm_hitalama_map_runtime_view_value( widget, fallback ),
				[ '$' + 'view_data' ]: ( key: string, fallback?: any )=> this.$.$shm_hitalama_map_runtime_view_data( widget, key, fallback ),
				[ '$' + 'bind_value' ]: ( id: string, fallback?: any )=> this.$.$shm_hitalama_map_runtime_bind_value( this.map(), id, fallback ),
				[ '$' + 'current_page' ]: current_page,
				get [ '$' + 'on_change' ]() {
					const runner = widget ? runtime.widget_runner( widget.link_str() ) : undefined
					return runner?.on_change.bind( runner )
				},
				[ '$' + 'arg' ]: context?.[ '$' + 'arg' ] ?? {},
				... context_public,
			}
		}

		source_js( code: string, sync: 'async' | 'sync', context_rich: Record< string, any > ) {
			return this.source_js_prefix( sync, context_rich ) + code
		}

		source_js_prefix( sync: 'async' | 'sync', context_rich: Record< string, any > ) {
			let vars = ''
			for( const key in context_rich ) vars += `const ${ key } = this[${ JSON.stringify( key ) }];\n`

			const widget = context_rich[ '$' + 'this_widget' ] as $shm_hitalama_map_widget | undefined
			const component_init = widget?.type() == 'component_view'
				? `Object.values($${''}map.components_by_name()).forEach( b => {
					b.code();
					$${''}map.widget_value_async(b.ref_str());
				} );\n`
				: ''

			return vars + component_init
		}

		@ $mol_action
		code_rich_js( code: string, sync: 'async' | 'sync', context_rich: Record< string, any > ) {
			return {
				code_rich: this.source_js( code, sync, context_rich ),
				context_rich,
				function_expression: false,
				source_meta: this.runtime_sourcemap().source_meta(
					this.function_header( context_rich, sync === 'async' ) + '\n' + this.source_js_prefix( sync, context_rich ),
				),
			}
		}

		@ $mol_action
		code_rich( code: string, sync: 'async' | 'sync', context?: Record< string, any > ) {
			const context_rich = this.context_rich( context, sync )
			if( this.runtime_ts().is_context( context_rich ) ) {
				return this.runtime_ts().code_rich( code, sync, context_rich )
			}
			return this.code_rich_js( code, sync, context_rich )
		}

		@ $mol_mem
		runtime_ts() {
			const obj = new this.$.$shm_hitalama_map_runtime_ts
			obj.runtime = ()=> this
			return obj
		}

		@ $mol_mem
		runtime_sourcemap() {
			const obj = new this.$.$shm_hitalama_map_runtime_sourcemap
			obj.runtime = ()=> this
			return obj
		}

		source_url(
			context: Record< string, any > | undefined,
			ext = 'js',
			widget = context?.[ '$' + 'this_widget' ] as $shm_hitalama_map_widget | undefined,
		) {
			const ref = widget?.link_str?.().replace( /[^a-zA-Z0-9_$.-]+/g, '_' ) || 'anonymous'
			const title = widget?.title?.().replace( /[^a-zA-Z0-9_$.-]+/g, '_' ) || 'code'
			return `hitalama://runtime/${ ref }/${ title }.${ ext }`
		}

		function_name( context: Record< string, any > | undefined, async: boolean ) {
			const widget = context?.[ '$' + 'this_widget' ] as $shm_hitalama_map_widget | undefined
			const ref = widget?.link_str?.().replace( /[^a-zA-Z0-9_$]+/g, '_' ) || 'anonymous'
			return `$shm_hitalama_map_runtime_${ async ? 'async' : 'sync' }_${ ref }`
		}

		function_source( body: string, context: Record< string, any >, async: boolean ) {
			return [
				this.function_header( context, async ),
				body,
				`})`,
			].join( '\n' )
		}

		function_header( context: Record< string, any >, async: boolean ) {
			const name = this.function_name( context, async )
			return `(${ async ? 'async ' : '' }function ${ name }(){`
		}

		script_with_stack( body: string, context: Record< string, any >, async: boolean ) {
			return this.runtime_sourcemap().script_with_source_url(
				this.function_source( body, context, async ),
				context,
			)
		}

		@ $mol_action
		func( code: string, context?: Record< string, any > ) {
			this.$.$shm_hitalama_compat_fqn_install( this.$ as any )
			const rich = this.code_rich( code, 'sync', context )
			const script = rich.function_expression
				? rich.code_rich
				: this.script_with_stack( rich.code_rich, rich.context_rich, false )
			this.runtime_sourcemap().stack_map_register( script, rich.source_meta )
			const func = ( 0, eval )( script ) as Function
			const context_rich = rich.context_rich
			return { func, context_rich }
		}

		execute( code: string, context?: Record< string, any > ) {
			const { func, context_rich } = this.func( code, context )
			try {
				return func.call( context_rich )
			} catch( error ) {
				this.runtime_sourcemap().error_stack_remap( error )
				throw error
			}
		}

		code_is_atom( code: string ) {
			return !code.includes( 'await' )
		}

		extract_reactive_deps( text: string ){
			let deps_code = ''
			const regex = new RegExp(`\\$(?:value|data|text|file|store|secret)\\s*\\(\\s*['"][^\\n]*?['"]\\s*\\)`, 'g')
			for( const match of text.matchAll( regex ) ) deps_code += match[ 0 ] + ';\n'

			const regex_for_text_deps = new RegExp(`\\$${''}value_async?\\(((?:[^\\n])*?),`, 'g')
			for( const match of text.matchAll( regex_for_text_deps ) ) {
				deps_code += `$${''}text(${ match[ 1 ].trim() });\n`
			}

			return deps_code
		}

		@ $mol_action
		func_async( code: string, context?: Record< string, any > ) {
			this.$.$shm_hitalama_compat_fqn_install( this.$ as any )
			const rich = this.code_rich( code, 'async', context )
			const script = rich.function_expression
				? rich.code_rich
				: this.script_with_stack( rich.code_rich, rich.context_rich, true )
			this.runtime_sourcemap().stack_map_register( script, rich.source_meta )
			const func = ( 0, eval )( script ) as Function
			const context_rich = rich.context_rich
			return { func, context_rich }
		}

		@ $mol_action
		execute_action( code: string, context?: Record< string, any > ) {
			if( this.code_is_atom( code ) ) return this.execute( code, context )

			const { func, context_rich } = this.func_async( code, context )
			try {
				return $mol_wire_sync( func ).call( context_rich )
			} catch( error ) {
				this.runtime_sourcemap().error_stack_remap( error )
				throw error
			}
		}

		to_table( obj: any ) {
			if( obj.head && obj.rows ) return {
				_format: 'table',
				table: obj
			}

			const arr = Array.isArray(obj) ? obj : [obj]
			const { head, rows } = $shm_hitalama_map_widget_entity_table.objects_to_table( arr )

			return {
				_format: 'table',
				table: { head, rows }
			}
		}

	}

	function create_molwire_dom(
		Class: new () => any,
		css_selectors: Record< string, string >,
		this_widget: $shm_hitalama_map_widget
	) {
		const link = this_widget.link_str()
		$mol_style_attach( `${ link }`, create_css( link, css_selectors ) )

		const w = new Class
		const atom = new $mol_wire_atom( link, () => w.App() )
		atom.fresh()

		const wrapper = document.createElement( 'div' )
		wrapper.appendChild( w.root() )

		wrapper.style.all = 'initial'
		wrapper.style.zIndex = '1'
		wrapper.style.backgroundColor = 'white'
		wrapper.setAttribute( 'data-crus-ref', link )
		return wrapper
	}

	function create_css( ref: string, css_selectors: Record< string, string > ): string {
		const cssRules: string[] = []
		for( const sel in css_selectors ) {
			cssRules.push( `[data-crus-ref="${ ref }"] ${ sel.trim() } { ${ css_selectors[ sel ].trim() } }` )
		}
		return cssRules.join( '\n' )
	}

}
